import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  Modal,
  Typography,
  IconButton,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';

import api from '../../../api/axiosInstance';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '../../loader';

interface Category {
  id: number;
  name: string;
  parentId: number | null;
}

export interface ModalAddCategoryProps {
  open: boolean;
  onClose: () => void;
  type: 'income' | 'expense';
  onAdded?: () => void;
  mode?: 'create' | 'edit';
  initial?: { id: number; name: string; parentId: number | null };
  onUpdated?: () => void;
}

export const ModalAddCategory: React.FC<ModalAddCategoryProps> = ({
  open,
  onClose,
  type,
  onAdded,
  mode = 'create',
  initial,
  onUpdated,
}) => {
  const queryClient = useQueryClient();

  const [name, setName] = useState<string>('');
  const [touchedName, setTouchedName] = useState(false);
  const [parentId, setParentId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0;

  const endpointBase = useMemo(
    () => (type === 'income' ? '/categories-income' : '/categories-expense'),
    [type],
  );

  const {
    data: categories = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<Category[]>({
    queryKey: ['categories', type],
    queryFn: async () => {
      const res = await api.get<Category[]>(endpointBase);
      return res.data;
    },
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initial) {
      setName(initial.name ?? '');
      setParentId(initial.parentId ?? null);
      setSelectedItem(
        typeof initial.parentId === 'number' && initial.parentId !== null
          ? String(initial.parentId)
          : null,
      );
    } else if (mode === 'create') {
      setName('');
      setParentId(null);
      setSelectedItem(null);
    }
  }, [open, mode, initial]);

  const buildTree = (
    items: Category[],
  ): Array<Category & { children: Array<Category & { children: any[] }> }> => {
    const map: Record<number, Category & { children: Category[] }> = {};
    items.forEach(item => {
      map[item.id] = { ...item, children: [] };
    });

    const roots: Array<Category & { children: Category[] }> = [];
    items.forEach(item => {
      if (item.parentId != null && map[item.parentId]) {
        map[item.parentId].children.push(map[item.id]);
      } else {
        roots.push(map[item.id]);
      }
    });

    return roots as Array<Category & { children: Array<Category & { children: any[] }> }>;
  };

  const treeData = useMemo(() => buildTree(categories), [categories]);
  const defaultExpanded = useMemo(() => treeData.map(n => String(n.id)), [treeData]);

  const renderTree = (
    nodes: Array<Category & { children: Array<Category & { children: any[] }> }>,
  ) =>
    nodes.map(node => (
      <TreeItem key={node.id} itemId={String(node.id)} label={node.name}>
        {node.children.length > 0 && renderTree(node.children)}
      </TreeItem>
    ));

  const handleSelect = (_: React.SyntheticEvent, itemId: string | null) => {
    if (itemId === null) {
      setSelectedItem(null);
      setParentId(null);
    } else {
      setSelectedItem(itemId);
      setParentId(Number(itemId));
    }
  };

  const resetState = () => {
    setName('');
    setParentId(null);
    setSelectedItem(null);
    setTouchedName(false);
  };

  const saveMutation = useMutation<void, Error>({
    mutationFn: async () => {
      if (!name.trim()) return;

      const payload = { name: name.trim(), parentId };

      if (mode === 'edit' && initial?.id) {
        await api.patch(`${endpointBase}/${initial.id}`, payload);
      } else {
        await api.post(endpointBase, payload);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories', type] });

      if (mode === 'edit') onUpdated?.();
      else onAdded?.();

      resetState();
      onClose();
    },
  });

  const handleSubmit = () => {
    setTouchedName(true);

    if (!isNameValid) return;
    if (saveMutation.isPending) return;

    saveMutation.mutate();
  };

  const title = mode === 'edit' ? 'Редактировать категорию' : 'Добавить категорию';
  const submitLabel = mode === 'edit' ? 'Сохранить' : 'Добавить';

  return (
    <Modal
      open={open}
      onClose={() => {
        if (saveMutation.isPending) return;
        onClose();
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 420 },
            maxWidth: 520,
            maxHeight: { xs: '92vh', sm: '86vh' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Typography variant="h6">{title}</Typography>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              flex: 1,
            }}
          >
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                label="Наименование категории"
                value={name}
                onChange={e => setName(e.target.value)}
                onBlur={() => setTouchedName(true)}
                error={touchedName && !isNameValid}
                required
                disabled={saveMutation.isPending}
              />
              <FormHelperText>
                {mode === 'edit' ? 'Измените имя категории' : 'Введите имя новой категории'}
              </FormHelperText>
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography>Родительская категория:</Typography>
              {selectedItem && (
                <IconButton
                  size="small"
                  disabled={saveMutation.isPending}
                  onClick={() => {
                    setSelectedItem(null);
                    setParentId(null);
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Box
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',

                height: { xs: '42vh', sm: 360 },
                minHeight: { xs: 260, sm: 320 },
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  px: 1,
                  py: 1,
                }}
              >
                {isLoading ? (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Loader />
                  </Box>
                ) : isError ? (
                  <Box display="flex" flexDirection="column" gap={1} alignItems="center" mt={2}>
                    <Typography color="error" variant="body2">
                      Ошибка загрузки категорий
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => refetch()}>
                      Повторить
                    </Button>
                  </Box>
                ) : (
                  <SimpleTreeView
                    selectedItems={selectedItem ?? undefined}
                    onSelectedItemsChange={handleSelect}
                    defaultExpandedItems={defaultExpanded}
                    slots={{
                      collapseIcon: ExpandMoreIcon,
                      expandIcon: ChevronRightIcon,
                    }}
                    sx={{
                      '& .MuiTreeItem-label': { fontSize: { xs: 14, sm: 14 } },
                    }}
                  >
                    {renderTree(treeData)}
                  </SimpleTreeView>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(0,0,0,0.06)',
              display: 'flex',
              gap: 1,
              flexDirection: 'column',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={!isNameValid || isLoading || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Сохранение…' : submitLabel}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={onClose}
              disabled={saveMutation.isPending}
            >
              Закрыть
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
